import { ReactiveController, ReactiveControllerHost } from 'lit';

export interface LoadMoreOnScrollControllerConfig {
  /**
   * This function is called when the sentinel is intersecting the scroll area.
   */
  onLoadMore?: () => Promise<void> | void;

  /**
   * Options to pass to the IntersectionObserver
   * (e.g. { root: null, rootMargin: '0px', threshold: 0.1 })
   * https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
   */
  observerOptions?: IntersectionObserverInit;

  /**
   * Optional function that returns the sentinel element
   * after the host has rendered. Alternatively, you can pass
   * a reference directly to the controller after firstUpdated.
   */
  getSentinelElement?: () => Element | null;
}

/**
 * A controller that triggers a callback when a sentinel element is scrolled into view.
 * Useful for implementing infinite scroll in Lit element components.
 */
export class LoadMoreOnScrollController implements ReactiveController {
  private host: ReactiveControllerHost;
  private onLoadMore?: () => Promise<void> | void;
  private observer?: IntersectionObserver;
  private getSentinelElement?: () => Element | null;
  private observerOptions: IntersectionObserverInit;

  constructor(host: ReactiveControllerHost, config: LoadMoreOnScrollControllerConfig) {
    this.host = host;
    this.onLoadMore = config.onLoadMore;
    this.observerOptions = config.observerOptions ?? {};
    this.getSentinelElement = config.getSentinelElement;

    host.addController(this);
  }

  hostConnected() {
    // We might not have the sentinel yet if the host hasn't rendered,
    // so we'll wait until the host updates to observe the sentinel
  }

  /**
   * `hostUpdated()` is called after the host updated & rendered
   */
  hostUpdated() {
    // Already have an observer? Do nothing
    if (this.observer) {
      return;
    }

    // Attempt to get the sentinel element
    const sentinel = this.getSentinelElement?.();

    if (!sentinel) {
      return;
    }

    // Create IntersectionObserver
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.onLoadMore?.();
      }
    }, this.observerOptions);

    this.observer.observe(sentinel);
  }

  hostDisconnected() {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
